import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { useSortable } from "../lib/useSortable";
import { toast } from "sonner";
import { ArrowLeft, ListChecks, Users, Camera, FileDown, Trash2, Layers, TrendingUp, TrendingDown, Minus, Award, Sparkles, RefreshCw, Target, ShieldCheck } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
} from "recharts";

const GROUP_LABELS = {
    fib: "FIB · Felicidade Interna Bruta",
    whr: "WHR · World Happiness Report",
    global: "Geral",
    custom: "Personalizadas",
};
const GROUP_COLOR = { fib: "#0d1628", whr: "#b89b6f", global: "#52525b", custom: "#8b5cf6" };

// Distinct colors for snapshot overlays (atual + up to 6 snapshots)
const SNAP_COLORS = ["#0d1628", "#b89b6f", "#8b5cf6", "#0e7490", "#dc2626", "#16a34a", "#f59e0b"];

export default function SurveyStatisticsPage() {
    const { id: pid, phase } = useParams();
    const phaseNum = Number(phase) || 1;
    const [data, setData] = useState(null);
    const [benchmark, setBenchmark] = useState(null);
    const [insights, setInsights] = useState(null);
    const [insightsBusy, setInsightsBusy] = useState(false);
    const [showSnapshots, setShowSnapshots] = useState(true);
    const [showBenchmark, setShowBenchmark] = useState(true);
    const [savingSnap, setSavingSnap] = useState(false);
    const [newLabel, setNewLabel] = useState("");

    const load = useCallback(() => {
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/statistics`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)));
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/benchmark`)
            .then((r) => setBenchmark(r.data))
            .catch(() => { /* benchmark is optional */ });
        api.get(`/projects/${pid}/surveys/phases/${phaseNum}/ai-insights`)
            .then((r) => setInsights(r.data))
            .catch(() => { /* none yet */ });
    }, [pid, phaseNum]);
    useEffect(() => { load(); }, [load]);

    const generateInsights = async (force = false) => {
        setInsightsBusy(true);
        try {
            const r = await api.post(
                `/projects/${pid}/surveys/phases/${phaseNum}/ai-insights${force ? "?force=true" : ""}`,
            );
            setInsights(r.data);
            toast.success(force ? "Insights regenerados" : "Insights gerados");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setInsightsBusy(false); }
    };

    const saveSnapshot = async () => {
        setSavingSnap(true);
        try {
            await api.post(`/projects/${pid}/surveys/phases/${phaseNum}/snapshots`, {
                label: newLabel || undefined,
            });
            toast.success("Snapshot guardado");
            setNewLabel("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSavingSnap(false); }
    };

    const deleteSnapshot = async (snapId) => {
        if (!window.confirm("Eliminar este snapshot? A ação é irreversível.")) return;
        try {
            await api.delete(`/projects/${pid}/surveys/phases/${phaseNum}/snapshots/${snapId}`);
            toast.success("Snapshot eliminado");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const indicesByGroup = useMemo(() => {
        if (!data) return {};
        const g = {};
        for (const i of data.indices) {
            if (i.score === null || i.score === undefined) continue;
            (g[i.group || "global"] ||= []).push(i);
        }
        return g;
    }, [data]);

    // Build snapshot overlay data per group
    const radarDataByGroup = useMemo(() => {
        if (!data) return {};
        const benchmarkByIdx = {};
        if (benchmark) {
            for (const b of benchmark.indices || []) benchmarkByIdx[b.index_id] = b;
        }
        const out = {};
        for (const [g, items] of Object.entries(indicesByGroup)) {
            const rows = items.map((i) => {
                const row = { name: i.name, atual: i.score };
                if (showBenchmark && benchmarkByIdx[i.index_id]) {
                    row.ecossistema = benchmarkByIdx[i.index_id].ecosystem_avg ?? null;
                }
                if (showSnapshots && data.snapshots) {
                    (data.snapshots || []).forEach((snap) => {
                        const match = (snap.indices || []).find((x) => x.index_id === i.index_id);
                        row[snap.id] = match?.score ?? null;
                    });
                }
                return row;
            });
            out[g] = rows;
        }
        return out;
    }, [data, indicesByGroup, showSnapshots, showBenchmark, benchmark]);

    if (!data) return <div className="label-caps" data-testid="stats-loading">A carregar...</div>;

    const snapshots = data.snapshots || [];

    return (
        <div className="space-y-10" data-testid="survey-stats-page">
            <div>
                <Link to={`/app/projects/${pid}/surveys/${phaseNum}`} className="label-caps inline-flex items-center gap-2" data-testid="back-to-survey">
                    <ArrowLeft size={12} /> Voltar ao inquérito
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
                    <h1 className="font-display text-5xl text-[#0d1628]">Estatísticas · Fase {phaseNum}</h1>
                    <a href={`${API}/projects/${pid}/surveys/phases/${phaseNum}/pdf?filled=true`} target="_blank" rel="noreferrer"
                        className="btn-outline" data-testid="pdf-filled-btn">
                        <FileDown size={14} /> PDF resultados
                    </a>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <Kpi icon={Users} label="Respondentes" value={data.respondents} />
                <Kpi icon={ListChecks} label="Respostas totais" value={data.total_responses} />
            </div>

            {/* Benchmark vs ecossistema */}
            {benchmark && benchmark.overall && benchmark.total_companies >= 1 && (
                <BenchmarkPanel benchmark={benchmark} show={showBenchmark} onToggle={setShowBenchmark} />
            )}

            {/* AI Insights */}
            {benchmark && benchmark.overall && (
                <AIInsightsPanel
                    insights={insights} busy={insightsBusy}
                    onGenerate={() => generateInsights(false)}
                    onRegenerate={() => generateInsights(true)}
                />
            )}

            {/* Snapshot controls */}
            <section className="border border-[#e4e4e7] p-5 bg-[#fdfcf9]" data-testid="snapshot-panel">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Layers size={16} className="text-[#b89b6f]" />
                        <div className="label-caps">Snapshots Históricos · {snapshots.length}</div>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[#52525b]" data-testid="snapshots-toggle-label">
                        <input type="checkbox" checked={showSnapshots} onChange={(e) => setShowSnapshots(e.target.checked)} data-testid="snapshots-toggle" />
                        Sobrepor nos gráficos
                    </label>
                </div>
                <p className="text-sm text-[#52525b] mt-2">
                    Guarde um snapshot agora para comparar com medições futuras (radar sobreposto). Útil no início, depois de cada fase e na avaliação final.
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-4">
                    <input
                        className="input-clean flex-1 min-w-[200px]"
                        placeholder="Etiqueta (ex.: Início, Após Fase 3)"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        data-testid="snapshot-label-input"
                    />
                    <button type="button" onClick={saveSnapshot} disabled={savingSnap || data.respondents === 0}
                        className="btn-primary" data-testid="save-snapshot-btn">
                        <Camera size={14} /> {savingSnap ? "A guardar..." : "Guardar snapshot"}
                    </button>
                </div>
                {snapshots.length > 0 && (
                    <div className="mt-4 space-y-2">
                        {snapshots.map((s, idx) => (
                            <div key={s.id} className="flex items-center justify-between border border-[#e4e4e7] px-3 py-2 bg-white" data-testid={`snapshot-${s.id}`}>
                                <div className="flex items-center gap-3">
                                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: SNAP_COLORS[(idx + 1) % SNAP_COLORS.length] }}></span>
                                    <div>
                                        <div className="text-sm font-medium text-[#0d1628]">{s.label}</div>
                                        <div className="text-xs text-[#52525b]">
                                            {new Date(s.taken_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} · {s.respondents} respondentes
                                        </div>
                                    </div>
                                </div>
                                <button type="button" onClick={() => deleteSnapshot(s.id)} className="text-[#52525b] hover:text-[#dc2626]" data-testid={`delete-snapshot-${s.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {data.respondents === 0 ? (
                <div className="border-l-4 border-[#b89b6f] bg-[#fdf8f0] p-6 text-[#52525b]" data-testid="empty-stats">
                    Ainda não foram registadas respostas. Os utilizadores precisam de submeter o inquérito para gerar estatísticas.
                </div>
            ) : (
                <>
                    {/* Radar by group with snapshot overlays */}
                    {Object.entries(indicesByGroup).map(([g, items]) => items.length >= 3 && (
                        <section key={g} data-testid={`radar-${g}`}>
                            <div className="label-caps mb-3" style={{ color: GROUP_COLOR[g] }}>{GROUP_LABELS[g] || g}</div>
                            <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 420 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarDataByGroup[g]}>
                                        <PolarGrid stroke="#e4e4e7" />
                                        <PolarAngleAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} />
                                        <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                                        <Radar name="Atual" dataKey="atual" stroke={SNAP_COLORS[0]} fill={SNAP_COLORS[0]} fillOpacity={0.35} />
                                        {showBenchmark && benchmark && (
                                            <Radar name="Ecossistema (média)" dataKey="ecossistema"
                                                stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1}
                                                strokeDasharray="4 4" />
                                        )}
                                        {showSnapshots && snapshots.map((s, idx) => (
                                            <Radar key={s.id} name={s.label} dataKey={s.id}
                                                stroke={SNAP_COLORS[(idx + 1) % SNAP_COLORS.length]}
                                                fill={SNAP_COLORS[(idx + 1) % SNAP_COLORS.length]}
                                                fillOpacity={0.15} />
                                        ))}
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Tooltip contentStyle={{ background: "#0d1628", color: "#fff", border: "none", fontSize: 12 }} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                    ))}

                    {/* Bar chart of indices (groups with < 3 indices) */}
                    {Object.entries(indicesByGroup).map(([g, items]) => items.length < 3 && items.length > 0 && (
                        <section key={`bar-${g}`}>
                            <div className="label-caps mb-3" style={{ color: GROUP_COLOR[g] }}>{GROUP_LABELS[g] || g}</div>
                            <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 240 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={items.map((i) => ({ name: i.name, score: i.score }))} layout="vertical" margin={{ left: 100 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                        <XAxis type="number" domain={[0, 5]} stroke="#52525b" />
                                        <YAxis type="category" dataKey="name" stroke="#52525b" style={{ fontSize: 11 }} />
                                        <Tooltip contentStyle={{ background: "#0d1628", color: "#fff", border: "none" }} />
                                        <Bar dataKey="score" fill={GROUP_COLOR[g]} radius={[0, 2, 2, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                    ))}

                    {/* Question-level breakdown */}
                    <section data-testid="questions-detail">
                        <div className="label-caps mb-4">Detalhe por pergunta</div>
                        <div className="space-y-3">
                            {data.questions.filter((q) => q.count > 0).map((q) => (
                                <div key={q.question_id} className="border border-[#e4e4e7] p-4" data-testid={`qstat-${q.question_id}`}>
                                    <div className="text-[#0d1628] font-medium">{q.text}</div>
                                    <div className="text-xs text-[#52525b] mt-1">{q.count} resposta(s) · {q.indices.join(", ") || "sem índice"}</div>
                                    <div className="mt-3">
                                        {q.type === "scale_1_5" && q.average !== undefined && (
                                            <div>
                                                <div className="font-display text-3xl text-[#b89b6f]">{q.average}/5</div>
                                                <div className="flex gap-1 mt-2">
                                                    {[1, 2, 3, 4, 5].map((n) => {
                                                        const c = q.distribution?.[String(n)] || 0;
                                                        const pct = q.count ? (c / q.count) * 100 : 0;
                                                        return (
                                                            <div key={n} className="flex-1 text-center" data-testid={`dist-${q.question_id}-${n}`}>
                                                                <div className="h-16 bg-[#f5f3ee] relative">
                                                                    <div className="absolute bottom-0 left-0 right-0 bg-[#0d1628]" style={{ height: `${pct}%` }}></div>
                                                                </div>
                                                                <div className="text-xs mt-1 text-[#52525b]">{n} · {c}</div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {q.type === "number" && q.average !== undefined && (
                                            <div className="flex gap-6 text-sm">
                                                <div><span className="label-caps">Média</span> <div className="font-display text-2xl">{q.average}</div></div>
                                                <div><span className="label-caps">Mín</span> <div className="font-display text-2xl">{q.min}</div></div>
                                                <div><span className="label-caps">Máx</span> <div className="font-display text-2xl">{q.max}</div></div>
                                            </div>
                                        )}
                                        {(q.type === "single_choice" || q.type === "multi_choice") && q.distribution && (
                                            <div className="space-y-1 mt-2">
                                                {Object.entries(q.distribution).sort(([, a], [, b]) => b - a).map(([k, v]) => {
                                                    const pct = q.count ? (v / q.count) * 100 : 0;
                                                    return (
                                                        <div key={k} className="text-sm">
                                                            <div className="flex justify-between mb-1">
                                                                <span className="text-[#0d1628]">{k}</span>
                                                                <span className="text-[#52525b]">{v} ({Math.round(pct)}%)</span>
                                                            </div>
                                                            <div className="h-1.5 bg-[#f5f3ee]">
                                                                <div className="h-full bg-[#b89b6f]" style={{ width: `${pct}%` }}></div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {q.type === "text" && q.samples && (
                                            <ul className="space-y-2 mt-2">
                                                {q.samples.map((s, i) => (
                                                    <li key={`${q.id}-sample-${i}-${(s || '').slice(0, 24)}`} className="text-sm text-[#52525b] border-l-2 border-[#b89b6f] pl-3">{s}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value }) {
    return (
        <div className="border border-[#e4e4e7] p-5">
            <div className="flex items-center gap-2 text-[#52525b]">
                <Icon size={14} className="text-[#b89b6f]" />
                <div className="label-caps">{label}</div>
            </div>
            <div className="mt-3 font-display text-3xl text-[#0d1628]">{value}</div>
        </div>
    );
}

function percentileLabel(p) {
    if (p === null || p === undefined) return "—";
    if (p >= 95) return "Top 5%";
    if (p >= 80) return "Top 20%";
    if (p >= 60) return "Acima da média";
    if (p >= 40) return "Na média";
    if (p >= 20) return "Abaixo da média";
    return "Bottom 20%";
}

function deltaIcon(delta) {
    if (delta === null || delta === undefined) return <Minus size={14} className="text-[#52525b]" />;
    if (delta > 0.1) return <TrendingUp size={14} className="text-[#16a34a]" />;
    if (delta < -0.1) return <TrendingDown size={14} className="text-[#dc2626]" />;
    return <Minus size={14} className="text-[#52525b]" />;
}

function BenchmarkPanel({ benchmark, show, onToggle }) {
    const overall = benchmark.overall;
    const indicesWithData = (benchmark.indices || []).filter((i) => i.my_score !== null);
    return (
        <section className="border border-[#e4e4e7] p-5 bg-[#fdf8f0]" data-testid="benchmark-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Award size={16} className="text-[#b89b6f]" />
                    <div className="label-caps">Benchmark · Ecossistema IHTP ({benchmark.total_companies} empresa{benchmark.total_companies !== 1 ? "s" : ""})</div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#52525b]">
                    <input type="checkbox" checked={show} onChange={(e) => onToggle(e.target.checked)} data-testid="benchmark-toggle" />
                    Mostrar linha do ecossistema no radar
                </label>
            </div>

            {/* Overall KPIs */}
            <div className="grid sm:grid-cols-4 gap-4 mt-4">
                <div className="bg-white border border-[#e4e4e7] p-4" data-testid="benchmark-my-score">
                    <div className="label-caps text-[#52525b]">O meu score</div>
                    <div className="font-display text-3xl text-[#0d1628] mt-1">{overall.my_score}<span className="text-sm text-[#52525b]">/5</span></div>
                </div>
                <div className="bg-white border border-[#e4e4e7] p-4" data-testid="benchmark-eco-avg">
                    <div className="label-caps text-[#52525b]">Média ecossistema</div>
                    <div className="font-display text-3xl text-[#b89b6f] mt-1">{overall.ecosystem_avg}<span className="text-sm text-[#52525b]">/5</span></div>
                </div>
                <div className="bg-white border border-[#e4e4e7] p-4" data-testid="benchmark-percentile">
                    <div className="label-caps text-[#52525b]">Posição</div>
                    <div className="font-display text-3xl text-[#0d1628] mt-1">{percentileLabel(overall.percentile)}</div>
                    <div className="text-xs text-[#52525b] mt-1">Percentil {overall.percentile}</div>
                </div>
                <div className="bg-white border border-[#e4e4e7] p-4" data-testid="benchmark-delta">
                    <div className="label-caps text-[#52525b]">Δ vs ecossistema</div>
                    <div className="flex items-center gap-2 mt-1">
                        {deltaIcon(overall.delta)}
                        <span className="font-display text-3xl text-[#0d1628]">{overall.delta > 0 ? "+" : ""}{overall.delta}</span>
                    </div>
                </div>
            </div>

            {/* Per-index table */}
            {indicesWithData.length > 0 && (
                <BenchmarkIndicesTable items={indicesWithData} />
            )}
            <p className="text-xs text-[#52525b] mt-3">
                Comparação calculada apenas com perguntas globais obrigatórias (FIB+WHR) para garantir coortes comparáveis entre empresas.
            </p>
        </section>
    );
}


const EFFORT_COLORS = {
    "Baixo": "bg-[#dcfce7] text-[#166534]",
    "Médio": "bg-[#fef3c7] text-[#92400e]",
    "Alto": "bg-[#fee2e2] text-[#991b1b]",
};

function AIInsightsPanel({ insights, busy, onGenerate, onRegenerate }) {
    const has = !!insights;
    return (
        <section className="border border-[#0d1628] bg-gradient-to-br from-[#0d1628] to-[#1a2942] text-white p-6" data-testid="ai-insights-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    <Sparkles size={20} className="text-[#b89b6f] mt-1" />
                    <div>
                        <div className="label-caps text-[#b89b6f]">IHTP Advisor · Insights por IA</div>
                        <h3 className="font-display text-3xl mt-1">Recomendações personalizadas</h3>
                        <p className="text-sm text-[#d4d4d8] mt-2 max-w-2xl">
                            Diagnóstico estratégico e plano de ação concreto baseado no benchmark da sua empresa, gerado por Claude Sonnet 4.5 com a metodologia IHTP.
                        </p>
                    </div>
                </div>
                {!has ? (
                    <button type="button" onClick={onGenerate} disabled={busy}
                        className="bg-[#b89b6f] hover:bg-[#a48a5f] text-[#0d1628] px-5 py-2.5 font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="generate-insights-btn">
                        <Sparkles size={14} /> {busy ? "A gerar... (45s)" : "Gerar insights"}
                    </button>
                ) : (
                    <button type="button" onClick={onRegenerate} disabled={busy}
                        className="border border-[#b89b6f] text-[#b89b6f] hover:bg-[#b89b6f] hover:text-[#0d1628] px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
                        data-testid="regenerate-insights-btn">
                        <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {busy ? "A regenerar..." : "Regenerar"}
                    </button>
                )}
            </div>

            {has && (
                <div className="mt-6 space-y-6">
                    {insights.executive_summary && (
                        <div className="bg-[#b89b6f]/10 border-l-4 border-[#b89b6f] p-4" data-testid="insights-summary">
                            <div className="label-caps text-[#b89b6f] mb-2">Resumo executivo</div>
                            <p className="text-[#fafafa] leading-relaxed">{insights.executive_summary}</p>
                        </div>
                    )}

                    {insights.recommendations && insights.recommendations.length > 0 && (
                        <div className="space-y-4" data-testid="insights-recommendations">
                            <div className="flex items-center gap-2">
                                <Target size={14} className="text-[#b89b6f]" />
                                <div className="label-caps text-[#b89b6f]">Plano de ação · {insights.recommendations.length} índices prioritários</div>
                            </div>
                            {insights.recommendations.map((rec, idx) => (
                                <div key={rec.index_id || idx} className="bg-white/5 border border-white/10 p-5" data-testid={`insight-rec-${rec.index_id}`}>
                                    <div className="flex items-baseline gap-3 flex-wrap">
                                        <span className="font-display text-2xl text-[#b89b6f]">{idx + 1}</span>
                                        <h4 className="font-display text-xl">{rec.index_name}</h4>
                                    </div>
                                    {rec.diagnosis && (
                                        <p className="text-sm text-[#d4d4d8] mt-2 italic">{rec.diagnosis}</p>
                                    )}
                                    <div className="grid md:grid-cols-3 gap-3 mt-4">
                                        {(rec.actions || []).map((a, ai) => (
                                            <div key={ai} className="bg-[#0d1628] border border-[#b89b6f]/30 p-4" data-testid={`action-${rec.index_id}-${ai}`}>
                                                <div className="text-[#b89b6f] font-medium text-sm">{a.title}</div>
                                                <p className="text-xs text-[#d4d4d8] mt-2 leading-relaxed">{a.description}</p>
                                                <div className="flex flex-wrap gap-1.5 mt-3">
                                                    {a.effort && (
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-sm ${EFFORT_COLORS[a.effort] || "bg-white/10 text-white"}`}>
                                                            {a.effort}
                                                        </span>
                                                    )}
                                                    {a.horizon && (
                                                        <span className="text-[10px] px-2 py-0.5 bg-white/10 text-white rounded-sm">
                                                            {a.horizon}
                                                        </span>
                                                    )}
                                                </div>
                                                {a.kpi && (
                                                    <div className="mt-3 pt-3 border-t border-white/10">
                                                        <div className="label-caps text-[10px] text-[#b89b6f]">KPI</div>
                                                        <p className="text-xs text-[#fafafa] mt-1">{a.kpi}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {insights.preserve && insights.preserve.length > 0 && (
                        <div className="bg-[#16a34a]/10 border-l-4 border-[#16a34a] p-4" data-testid="insights-preserve">
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck size={14} className="text-[#16a34a]" />
                                <div className="label-caps text-[#16a34a]">A preservar</div>
                            </div>
                            <ul className="space-y-1.5">
                                {insights.preserve.map((p, i) => (
                                    <li key={`preserve-${i}-${(p || '').slice(0, 24)}`} className="text-sm text-[#fafafa]">• {p}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="text-xs text-[#a1a1aa] flex items-center gap-2">
                        <Sparkles size={10} className="text-[#b89b6f]" />
                        Gerado por {insights.model} em {new Date(insights.generated_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} · Cache automático até o benchmark mudar.
                    </div>
                </div>
            )}

            {!has && !busy && (
                <p className="text-sm text-[#a1a1aa] mt-4">
                    A geração consome créditos do plano IHTP e demora ~45 segundos. O resultado fica em cache até as respostas mudarem.
                </p>
            )}
        </section>
    );
}


function SortableTh({ label, k, sortKey, sortDir, onSort, className = "text-left p-2", align = "left" }) {
    const active = sortKey === k;
    const arrow = active ? (sortDir === "asc" ? "↑" : "↓") : "↕";
    const justify = align === "right" ? "justify-end" : "justify-start";
    return (
        <th className={className} data-testid={`bench-sort-${k}`}>
            <button type="button" onClick={() => onSort(k)}
                className={`label-caps w-full inline-flex items-center gap-1 ${justify} ${active ? "text-[#0d1628]" : "text-[#52525b] hover:text-[#0d1628]"}`}>
                {label}
                <span className={`text-[10px] ${active ? "text-[#b89b6f]" : "text-[#a1a1aa]"}`}>{arrow}</span>
            </button>
        </th>
    );
}

function BenchmarkIndicesTable({ items }) {
    const accessors = useMemo(() => ({
        name: (r) => r.name,
        my_score: (r) => r.my_score ?? -1,
        ecosystem_avg: (r) => r.ecosystem_avg ?? -1,
        delta: (r) => r.delta ?? -999,
        percentile: (r) => r.percentile ?? -1,
    }), []);
    const { items: sortedItems, sortKey, sortDir, requestSort } =
        useSortable(items, "delta", "asc", accessors);
    return (
        <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="benchmark-indices-table">
                <thead className="text-[#52525b]">
                    <tr className="border-b border-[#e4e4e7]">
                        <SortableTh label="Índice" k="name" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                        <SortableTh label="O meu" k="my_score" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-2" align="right" />
                        <SortableTh label="Ecossistema" k="ecosystem_avg" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-2" align="right" />
                        <SortableTh label="Δ" k="delta" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-2" align="right" />
                        <SortableTh label="Posição" k="percentile" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-right p-2" align="right" />
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#e4e4e7]">
                    {sortedItems.map((b) => (
                        <tr key={b.index_id} data-testid={`benchmark-row-${b.index_id}`}>
                            <td className="p-2 text-[#0d1628]">{b.name}</td>
                            <td className="p-2 text-right font-display">{b.my_score}</td>
                            <td className="p-2 text-right text-[#b89b6f]">{b.ecosystem_avg ?? "—"}</td>
                            <td className="p-2 text-right">
                                <span className="inline-flex items-center gap-1">
                                    {deltaIcon(b.delta)}
                                    <span>{b.delta !== null && b.delta > 0 ? "+" : ""}{b.delta ?? "—"}</span>
                                </span>
                            </td>
                            <td className="p-2 text-right text-xs text-[#52525b]">{percentileLabel(b.percentile)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
